import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = unauth, object = auth
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("paiddewz_token", data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      // Preserve the original axios error so callers can inspect status/response
      const wrapped = new Error(msg);
      wrapped.response = e.response;
      wrapped.status = e.response?.status;
      wrapped.original = e;
      throw wrapped;
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await http.post("/auth/register", payload);
      if (data.access_token) localStorage.setItem("paiddewz_token", data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("paiddewz_token");
    setUser(null);
  };

  const setAuthFromIntake = (userObj, token) => {
    if (token) localStorage.setItem("paiddewz_token", token);
    setUser(userObj);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, fetchMe, setAuthFromIntake }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
